import { categories } from '@/constants/categories';

import type { IClaim } from '@/types/claim';

import { formatCurrency, formatDate } from '@/utils/formatting';

import { StatusLabel } from './StatusLabel';

interface ClaimsListProps {
  claims: IClaim[];
  isMobile?: boolean;
  hasAnimated?: boolean;
  isVisible?: boolean;
  showIcon?: boolean;
  onClaimClick?: (claim: IClaim) => void;
}

export function ClaimsList({
  claims,
  isMobile = false,
  hasAnimated = false,
  isVisible = true,
  showIcon = true,
  onClaimClick,
}: ClaimsListProps) {
  const getCategoryInfo = (categoryId: number) => {
    return categories.find((cat) => cat.id === categoryId);
  };

  return (
    <div className={`space-y-0 ${!isMobile ? 'custom-scrollbar flex-1 overflow-y-auto' : ''}`}>
      {claims.map((claim, index) => {
        const categoryInfo = getCategoryInfo(claim.category);

        return (
          <div
            key={claim.id}
            data-testid="claim-row"
            onClick={() => onClaimClick?.(claim)}
            onKeyDown={(e) => {
              if ((e.key === 'Enter' || e.key === ' ') && onClaimClick) {
                e.preventDefault();
                onClaimClick(claim);
              }
            }}
            role={onClaimClick ? 'button' : undefined}
            tabIndex={onClaimClick ? 0 : undefined}
            className={`flex items-center gap-4 border-b border-gray-100 px-0 py-4 hover:bg-gray-50 ${
              hasAnimated
                ? ''
                : `transition-all duration-500 ${
                    isVisible ? 'translate-x-0 opacity-100' : 'translate-x-[-20px] opacity-0'
                  }`
            }`}
            style={
              hasAnimated
                ? undefined
                : {
                    transitionDelay: `${300 + index * 50}ms`,
                  }
            }
          >
            {categoryInfo?.icon ? (
              <div
                className={`size-12 ${categoryInfo.iconBg || 'bg-gray-100'} ${categoryInfo.iconText || 'text-gray-600'} flex shrink-0 items-center justify-center rounded-full`}
              >
                <categoryInfo.icon size={24} strokeWidth={2} />
              </div>
            ) : (
              <div className="flex size-12 shrink-0 items-center justify-center rounded-full bg-gray-100">
                <span className="text-lg">{categoryInfo?.emoji || '📄'}</span>
              </div>
            )}

            <div className="min-w-0 flex-1 overflow-hidden">
              <div
                className="mb-1 truncate text-lg font-medium text-gray-900"
                data-testid="claim-row-title"
              >
                {claim.details || 'No description'}
              </div>
              <div
                className="text-sm wrap-break-word break-all text-gray-600"
                data-testid="claim-row-meta"
              >
                {categoryInfo?.name || 'Unknown'} • {formatDate(claim.createdAt)}
              </div>
            </div>

            <div className="hidden shrink-0 items-center gap-4 md:flex">
              <div className="text-base font-semibold text-gray-900">
                {formatCurrency(claim.amount, true)}
              </div>

              <StatusLabel status={claim.status} flag={claim.flag} />
            </div>

            <div className="flex shrink-0 flex-col items-end gap-2 md:hidden">
              <div className="text-base font-semibold wrap-break-word text-gray-900">
                {formatCurrency(claim.amount, true)}
              </div>

              <StatusLabel
                status={claim.status}
                flag={claim.flag}
                className="rounded"
                showIcon={showIcon}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}
